import * as THREE from 'three';
import { BOSS_RADIUS, type Boss } from '../ai/boss';
import type { Projectile, ProjectilePool } from './projectiles';
import { applyDamage, type Health } from './damage';
import { withinEngagementRange } from './spatial';
import type { MeleeSwing } from './weapon';
import type { DamageEvent } from './resolve';
import type { BlockPhysics } from '../world/collision';

/**
 * Boss-specific parallels to `combat/resolve.ts`'s enemy-damage functions —
 * NOT additions to that file, because `Boss` (ai/boss.ts) is deliberately
 * NOT an `Enemy` (see boss.ts's own header comment for why). Each function
 * here mirrors its `resolve.ts` counterpart's exact discipline (i-frame
 * gating, fire-time-captured damage, knockback direction) against a single
 * `Boss | null` instead of an enemy array.
 */

const PLAYER_HIT_INVULN_MS = 250; // matches resolve.ts's own constant — same post-hit grace window regardless of what hit the player

/**
 * K-1(c): "player shots require line-of-sight (segmentClear) so the
 * through-the-fence exploit dies." Checked once per call (not per-
 * projectile — it's the same fixed geometric question every time) from the
 * PLAYER's own position to the boss, not from wherever an individual
 * projectile's simulated position happens to be: bullets in this game don't
 * collide with world geometry, so a bullet fired "through" a solid fence
 * can already report a position on the far side by the time it's tested —
 * only a check anchored at the shooter's real position catches the fence
 * standing directly between them. Scoped to ranged attacks (bullets/pellets
 * and the super blast) per PLAN-CITY3 §4's own "player SHOTS" wording —
 * melee's short range/facing-arc gate already makes a through-the-fence
 * abuse a non-issue, so `resolveMeleeHitsOnBoss` is deliberately left
 * ungated here.
 */
function hasLineOfSightToBoss(playerPos: THREE.Vector3, boss: Boss, physics: BlockPhysics): boolean {
  return physics.segmentClear(playerPos.x, playerPos.z, boss.position.x, boss.position.z, boss.position.y + 1.2);
}

/**
 * Player bullets/pellets vs. the boss. Must run AFTER
 * `resolve.ts`'s `resolveProjectileHitsOnEnemies` in the same tick (both
 * consume the SAME `active` array from `ProjectilePool.update()`) — that
 * function has no `.active` guard of its own (it was written before a boss
 * existed to share the array with, and changing it now risks an
 * already-reviewed function for no reason). THIS function guards on
 * `proj.active` instead, so a shot the enemy-pass already consumed this
 * tick can never ALSO land on the boss.
 */
export function resolveProjectileHitsOnBoss(active: Projectile[], boss: Boss | null, pool: ProjectilePool, physics: BlockPhysics, playerPos: THREE.Vector3): DamageEvent[] {
  if (!boss || boss.state === 'dead') return [];
  if (!hasLineOfSightToBoss(playerPos, boss, physics)) return [];
  const events: DamageEvent[] = [];
  // Review finding: a multi-projectile volley (Sawed-Off's 5 pellets, all
  // landing the same tick at point-blank range) that kills the boss partway
  // through kept processing every LATER pellet as a full phantom "hit"
  // event (extra super-meter credit, an extra 'hit' sfx firing right after
  // 'kill') — `takeDamage`'s own dead-guard correctly no-ops the damage,
  // but this function still pushed an event for it regardless. Tracked via
  // the ALREADY-AVAILABLE `killed` return value (not a re-read of
  // `boss.state`, which TS's control-flow narrowing treats as statically
  // excluding 'dead' for the rest of this function after the early-return
  // guard above, making a direct re-check here a false "unreachable"
  // error) — the pellet that actually lands the killing blow is still
  // processed normally; only the ones AFTER it in this same array stop.
  let bossDefeated = false;
  for (const proj of active) {
    if (bossDefeated) break;
    if (!proj.active) continue; // already consumed by resolve.ts's enemy pass this same tick
    if (proj.owner !== 'player') continue;
    if (!withinEngagementRange(boss.position, proj.position, BOSS_RADIUS + proj.radius)) continue;
    const knockDir = proj.velocity.clone().setY(0);
    const killed = boss.takeDamage(proj.damage, knockDir, proj.knockbackForce);
    if (killed) bossDefeated = true;
    pool.deactivate(proj);
    events.push({ killed, scoreValue: 0, crit: proj.crit, position: boss.position.clone() });
  }
  return events;
}

/** Player melee (Stickball Bat) vs. the boss — same arc/facing gate `resolveMeleePlayerHits` applies to enemies, since a swing has no shared-consumable resource, this can run independently of (before or after) that function with no ordering requirement. */
export function resolveMeleeHitsOnBoss(swing: MeleeSwing, boss: Boss | null, playerPos: THREE.Vector3, facing: number): DamageEvent[] {
  if (!boss || boss.state === 'dead') return [];
  if (!withinEngagementRange(boss.position, playerPos, swing.range + BOSS_RADIUS)) return [];
  const forward = new THREE.Vector3(Math.sin(facing), 0, Math.cos(facing));
  const toBoss = new THREE.Vector3().subVectors(boss.position, playerPos).setY(0);
  if (toBoss.lengthSq() > 1e-6 && toBoss.angleTo(forward) > swing.arcHalfAngle) return [];
  const knockDir = toBoss.lengthSq() > 1e-6 ? toBoss : forward.clone();
  const killed = boss.takeDamage(swing.damage, knockDir, swing.knockbackForce);
  return [{ killed, scoreValue: 0, crit: swing.crit, position: boss.position.clone() }];
}

const SUPER_BOSS_KNOCKBACK = 4; // Gridlock is a boss, not a roster enemy — SUPER_CONSTANTS.knockback (tuned for light enemies) would visually over-launch something this size; a smaller fixed value reads better without touching the shared constant

/** The player's super blast vs. the boss — no `superMeter` argument, same asymmetry `applySuperBlast` (resolve.ts) already documents: consuming the whole meter for one blast can't partially refund itself from its own kill. K-1(c): same line-of-sight gate as `resolveProjectileHitsOnBoss` — the blast is centered on the player, so a fenced-off boss shouldn't be reachable by it either. */
export function applySuperBlastOnBoss(playerPos: THREE.Vector3, boss: Boss | null, radius: number, damage: number, physics: BlockPhysics): DamageEvent | null {
  if (!boss || boss.state === 'dead') return null;
  if (!hasLineOfSightToBoss(playerPos, boss, physics)) return null;
  if (!withinEngagementRange(boss.position, playerPos, radius)) return null;
  const dir = new THREE.Vector3().subVectors(boss.position, playerPos).setY(0);
  const killed = boss.takeDamage(damage, dir, SUPER_BOSS_KNOCKBACK);
  return { killed, scoreValue: 0, crit: false, position: boss.position.clone() };
}

const SHOCKWAVE_KNOCKBACK = 5.5;

/**
 * Gridlock's attack1 (shockwave) vs. the player — a stationary ground-slam
 * AoE centered on the boss, resolved the instant `Boss.update()` reports
 * `shockwaveFired` for that tick (main.ts). Same `slideInvulnerable`/
 * i-frame discipline as `resolve.ts`'s `resolveMeleeAttacksOnPlayer`
 * (the boss's own "melee" equivalent — a fixed-range AoE rather than a
 * per-enemy contact check, but functionally the same "did this land" shape).
 */
export function resolveBossShockwaveOnPlayer(bossPos: THREE.Vector3, radius: number, damage: number, playerPos: THREE.Vector3, playerHealth: Health, slideInvulnerable: boolean): boolean {
  if (slideInvulnerable) return false;
  if (!withinEngagementRange(bossPos, playerPos, radius)) return false;
  const wasVulnerable = playerHealth.invulnerableT <= 0 && playerHealth.hp > 0;
  const knockDir = new THREE.Vector3().subVectors(playerPos, bossPos).setY(0);
  applyDamage(playerHealth, damage, { knockbackDir: knockDir, knockbackForce: SHOCKWAVE_KNOCKBACK, invulnerableAfterMs: PLAYER_HIT_INVULN_MS });
  return wasVulnerable;
}

/**
 * K-1(a): "hubcap broken by shooting it down in flight" — player bullets vs.
 * a `breakable` enemy projectile (only Gridlock's hubcap sets this today).
 * Scans the SAME shared `active` array `resolveProjectileHitsOnBoss`/
 * `resolve.ts`'s enemy pass already consume from, guarded on `.active`
 * exactly like those (a bullet consumed by an earlier pass this tick can't
 * ALSO break the hubcap). Returns true the tick the tracked hubcap is
 * actually destroyed this way — main.ts uses that to call
 * `Boss.reportHubcapBroken()`. No line-of-sight gate here (unlike the
 * boss-damage functions above): the hubcap is already IN the open pocket by
 * the time it's breakable, not behind the fence.
 */
export function resolveProjectileHitsOnBreakableProjectiles(active: Projectile[], pool: ProjectilePool): boolean {
  let brokeAny = false;
  for (const enemyProj of active) {
    if (!enemyProj.active || enemyProj.owner !== 'enemy' || !enemyProj.breakable) continue;
    for (const playerProj of active) {
      // Review fix (MEDIUM): the original unconditional `break` after the
      // FIRST in-range player projectile silently dropped every OTHER
      // simultaneously-in-range pellet from a multi-projectile burst
      // (Sawed-Off's 5 pellets) — HUBCAP_BREAK_HP's own comment ("a couple
      // pellets" should break it) assumes a same-tick burst can actually
      // land more than one hit. Stop consuming further player projectiles
      // only once the enemy projectile is ALREADY destroyed this tick —
      // matching resolveProjectileHitsOnBoss's own "the pellet that lands
      // the kill is processed normally; only the ones AFTER it stop"
      // precedent — rather than capping at exactly one hit regardless.
      if (!enemyProj.active) break;
      if (!playerProj.active || playerProj.owner !== 'player') continue;
      if (!withinEngagementRange(enemyProj.position, playerProj.position, enemyProj.radius + playerProj.radius)) continue;
      enemyProj.hp -= playerProj.damage;
      pool.deactivate(playerProj);
      if (enemyProj.hp <= 0) {
        pool.deactivate(enemyProj);
        brokeAny = true;
      }
    }
  }
  return brokeAny;
}
